/*
 * Fig & Bloom - Reminders as newsletter capture (business-os #157).
 *
 * Two storefront surfaces, each tagged with a `source` for the baseline split
 * Data pulls (footer, confirmation, card_step, other):
 *
 *   footer     sections/newsletter.liquid, "Never forget a date, join Reminders."
 *              The email form no longer posts to the Shopify newsletter list. On
 *              submit it opens the Reminders sheet at the verify-flow identify
 *              step with the email filled in, and credits what follows in the
 *              sheet to `footer` (sessionStorage fb_rem_source, the convention
 *              snippets/fb-reminders-sheet.liquid already uses).
 *              Event: reminders_footer_signup_submitted
 *
 *   card_step  snippets/fb-reminders-card-optin.liquid, "Remember this for next
 *              year?" under the card message on the cart page. Unticked by
 *              default; the choice rides on the cart attribute
 *              _fb_remember_next_year ("yes" / "") so it survives checkout.
 *              Nothing is saved here: Reminders saves the recipient and
 *              occasion only after a paid order.
 *              Event: reminders_card_optin_changed
 *
 * Analytics follow assets/fb-reminders.js's trackReminderEvent: snake_case
 * names, surface/touchpoint/flow_version on every event, a guarded
 * posthog.capture that never throws, and nothing a customer typed (no email,
 * no name, no card text) in any payload. Only fixed strings and a boolean.
 *
 * Tests: tests/fb-reminders-capture.test.js
 */
(function (w, d) {
  if (w.FBRemindersCapture) return;

  var FLOW_VERSION = 'v1';
  var ATTRIBUTE = '_fb_remember_next_year';
  var SOURCES = { footer: 1, card_step: 1, confirmation: 1, other: 1 };

  function track(name, source, extra) {
    try {
      if (!w.posthog || typeof w.posthog.capture !== 'function') return;
      if (!SOURCES[source]) source = 'other';
      var payload = { surface: 'storefront', touchpoint: source, source: source, flow_version: FLOW_VERSION };
      if (extra) Object.keys(extra).forEach(function (k) { if (typeof extra[k] === 'boolean') payload[k] = extra[k]; });
      w.posthog.capture(name, payload);
    } catch (_) {}
  }

  function setSource(source) {
    try { w.sessionStorage.setItem('fb_rem_source', source); } catch (_) {}
  }

  /* ---------- footer ---------- */
  function onFooterSubmit(event) {
    var form = event.target && event.target.closest && event.target.closest('[data-fb-reminders-footer-form]');
    if (!form) return;
    var opener = form.querySelector('[data-fb-reminders-open]');
    // Without the Reminders sheet on the page, fall through to the form's own
    // action (/pages/reminders; the email input has no name, so nothing typed
    // lands in the URL).
    if (!opener || !d.getElementById('fb-reminders-sheet')) return;
    event.preventDefault();
    var input = form.querySelector('input[type="email"]');
    if (input && typeof input.checkValidity === 'function' && input.value && !input.checkValidity()) {
      if (typeof input.reportValidity === 'function') input.reportValidity();
      return;
    }
    track('reminders_footer_signup_submitted', 'footer', { email_entered: !!(input && input.value.trim()) });
    // The sheet's own click listener credits fb_rem_source from
    // data-fb-reminders-touchpoint; set it here too in case it is not bound.
    setSource('footer');
    opener.click();
  }

  /* ---------- footer copy link (default) ----------
     "Never forget a date, join Reminders." beside the newsletter form. The
     link navigates normally (/pages/reminders?rem_src=footer); this only
     records the click. */
  function onFooterLinkClick(event) {
    var link = event.target && event.target.closest && event.target.closest('[data-fb-reminders-footer-link]');
    if (!link) return;
    track('reminders_footer_link_clicked', 'footer');
    setSource('footer');
  }

  /* ---------- card step: members only (v1) ----------
     Uses the theme's existing Reminders signed-in check, never a call of its
     own: the fb-reminders:state event assets/fb-reminders-welcome.js
     dispatches after GET /api/v1/me, and the same fb_rem_me sessionStorage
     cache ({expiresAt, data}) it and assets/fb-reminders-api.js share. Fails
     closed: until one of them says signedIn with a member, the opt-in stays
     hidden and disabled, and a change is ignored without touching the cart.
     A transport error (including the CORS block on *.shopifypreview.com)
     produces no answer, so it stays hidden. */
  var member = false;

  function isMember(me) {
    return !!(me && me.signedIn === true && me.member && typeof me.member === 'object');
  }

  function cachedMe() {
    try {
      var cached = JSON.parse(w.sessionStorage.getItem('fb_rem_me') || 'null');
      return cached && cached.data && cached.expiresAt > Date.now() ? cached.data : null;
    } catch (_) { return null; }
  }

  function applyMember(me) {
    member = isMember(me);
    var nodes = d.querySelectorAll ? d.querySelectorAll('[data-fb-reminders-card-optin]') : [];
    Array.prototype.forEach.call(nodes, function (wrap) {
      var box = wrap.querySelector('input[type="checkbox"]');
      if (member) { wrap.hidden = false; if (box) box.disabled = false; }
      else { wrap.hidden = true; if (box) box.disabled = true; }
    });
  }

  function cartUpdate(checked) {
    var attrs = {}; attrs[ATTRIBUTE] = checked ? 'yes' : '';
    var root = (w.Shopify && w.Shopify.routes && w.Shopify.routes.root) || '/';
    return w.fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); });
  }

  function onCardOptinChange(event) {
    var box = event.target;
    if (!box || !box.matches || !box.matches('[data-fb-reminders-card-optin] input[type="checkbox"]')) return;
    var wrap = box.closest('[data-fb-reminders-card-optin]');
    if (!member) { box.checked = false; return Promise.resolve(); }
    var checked = !!box.checked;
    track('reminders_card_optin_changed', 'card_step', { checked: checked });
    if (checked) setSource('card_step');
    if (wrap) wrap.setAttribute('aria-busy', 'true');
    return cartUpdate(checked).then(function () {
      if (wrap) { wrap.removeAttribute('aria-busy'); wrap.removeAttribute('data-error'); }
    }).catch(function () {
      // Keep what the customer sees honest: revert if the cart did not take it.
      box.checked = !checked;
      if (wrap) { wrap.removeAttribute('aria-busy'); wrap.setAttribute('data-error', ''); }
    });
  }

  d.addEventListener('submit', onFooterSubmit, true);
  d.addEventListener('change', onCardOptinChange);
  d.addEventListener('click', onFooterLinkClick, true);
  d.addEventListener('fb-reminders:state', function (event) { try { applyMember(event && event.detail && event.detail.me); } catch (_) { applyMember(null); } });
  function boot() { try { applyMember(cachedMe()); } catch (_) { applyMember(null); } }
  if (d.readyState === 'loading' && d.addEventListener) d.addEventListener('DOMContentLoaded', boot); else boot();

  w.FBRemindersCapture = {
    _test: { track: track, onFooterLinkClick: onFooterLinkClick, applyMember: applyMember, isMember: isMember, cachedMe: cachedMe, isMemberNow: function () { return member; }, onFooterSubmit: onFooterSubmit, onCardOptinChange: onCardOptinChange, ATTRIBUTE: ATTRIBUTE, FLOW_VERSION: FLOW_VERSION }
  };
})(window, document);
